import { Markdown } from 'tiptap-markdown'
import { Typography } from '@tiptap/extension-typography'
import { Subscript } from '@tiptap/extension-subscript'
import { Superscript } from '@tiptap/extension-superscript'
import { Extension, Extensions, InputRule } from '@tiptap/core'
import { Highlight } from '@tiptap/extension-highlight'
import { Placeholder } from '@tiptap/extension-placeholder'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { HorizontalRule } from '@tiptap/extension-horizontal-rule'
import { BubbleMenu } from '@tiptap/extension-bubble-menu'
import { UploadVideosPlugin } from '../../plugins/upload-video'
import { BaseHubBlock } from './extensions/basehub-block/index'
import {
  Kbd,
  Strike,
  Blockquote,
  Bold,
  BulletList,
  Dropcursor,
  Gapcursor,
  HardBreak,
  Heading,
  Italic,
  ListItem,
  OrderedList,
  Document,
  Text,
} from '@basehub/tiptap-api-utils'
import {
  CollaborationExtensionsOptions,
  getCollaborationExtensions,
} from '../collaboration'
import { Video } from './extensions/video'
import { SlashCommands } from './extensions/slash-command'
import { type SlashCommandType } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/slash-command'
import { BaseHubInlineBlock } from './extensions/basehub-simple-block'
import { Link } from './extensions/link'
import { CodeBlockWithLanguage } from './extensions/code-block'
import { Table } from './extensions/table'
import { Paragraph } from '@tiptap/extension-paragraph'
import { CodeMark } from './extensions/inline-mark'
import { FakeCursor } from './extensions/fake-cursor'
import { LinterExtensionOptions, getLinterExtension } from './extensions/linter'
import { SearchAndReplace } from './extensions/find-and-replace'
import { CustomImage } from './extensions/updated-image'
import {
  PasteHandler,
  PasteHandlerExtension,
} from './extensions/paste-priority-handler'
import { Underline } from '@tiptap/extension-underline'
import { Plugin } from '@tiptap/pm/state'
import { DeleteFirstEmptyParagraphExtension } from './extensions/delete-first-empty-paragraph'

// import { Autocomplete } from './extensions/autocomplete'

export type RichTextExtensionsOptions = {
  collaborationExtensionsOptions: CollaborationExtensionsOptions
  linterExtensionOptions: LinterExtensionOptions
  slashCommand: Pick<
    SlashCommandType,
    'component' | 'onSelectBlock' | 'richTextId'
  >
  onClickBshbInstanceNode: (id: string) => unknown
  pasteHandlers?: PasteHandler[]
  redirect?: (url: string) => void
}

// need to be in sync with richTextExtensionsForTransformations (server side only transformations)
export const getRichTextExtensions = (options: RichTextExtensionsOptions) => {
  return [
    // core
    Document,
    Text,
    // nodes
    Paragraph.extend({
      addKeyboardShortcuts() {
        return {
          // prevents creating a new line break when using the create block underneath shortcut
          'Mod-Enter': () => true,
        }
      },
    }).configure({
      HTMLAttributes: {
        class: 'rt-paragraph',
      },
    }),
    Blockquote.configure({
      HTMLAttributes: {
        // class: 'border-dark-border-solid pl-3 rounded-l-sm',
        class: 'rt-blockquote',
        style: 'quotes: none',
      },
    }),
    CodeBlockWithLanguage.configure({
      HTMLAttributes: {
        class: 'rt-codeblock  ',
      },
    }),
    Heading.configure({
      levels: [1, 2, 3, 4, 5, 6],
      HTMLAttributes: {
        class: 'rt-heading',
      },
    }),
    BaseHubBlock,
    CustomVideo.configure({
      HTMLAttributes: {
        class: 'rt-video',
      },
    }),
    // patch to fix horizontal rule bug: https://github.com/ueberdosis/tiptap/pull/3859#issuecomment-1536799740
    HorizontalRule.extend({
      addInputRules() {
        return [
          new InputRule({
            find: /^(?:---|—-|___\s|\*\*\*\s)$/,
            handler: ({ state, range }) => {
              const attributes = {}

              const { tr } = state
              const start = range.from
              const end = range.to

              tr.insert(start - 1, this.type.create(attributes) as any).delete(
                tr.mapping.map(start),
                tr.mapping.map(end)
              )
            },
          }),
        ]
      },
    }).configure({
      HTMLAttributes: {
        // class: 'my-4 border-t border-dark-border-solid',
        class: 'rt-horizontal-rule',
      },
    }),
    CustomImage.configure({
      HTMLAttributes: {
        class: 'rt-image',
      },
    }),
    // marks
    Strike.configure({
      HTMLAttributes: {
        class: 'rt-strike',
      },
    }),
    Bold.configure({
      HTMLAttributes: {
        class: 'rt-bold',
      },
    }),
    Italic.configure({
      HTMLAttributes: {
        class: 'rt-italic',
      },
    }),
    CodeMark.configure({
      HTMLAttributes: {
        class: 'rt-code',
      },
    }),
    Kbd.configure({
      HTMLAttributes: {
        class: 'rt-kbd',
      },
    }),
    Superscript.configure({
      HTMLAttributes: {
        class: 'rt-superscript',
      },
    }),
    Subscript.configure({
      HTMLAttributes: {
        class: 'rt-subscript',
      },
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: 'rt-link',
      },
      redirect: options.redirect,
    }),
    // find and replace
    SearchAndReplace,
    BaseHubInlineBlock.configure({
      HTMLAttributes: {
        class: 'rt-basehub-inline-block',
      },
      onClickBasehubInstanceMark: options.onClickBshbInstanceNode,
    }),
    Highlight.configure({
      multicolor: true,
      HTMLAttributes: {
        class: 'rt-highlight',
      },
    }),
    // lists
    BulletList.configure({
      HTMLAttributes: {
        class: 'rt-bullet-list',
      },
    }),
    OrderedList.configure({
      HTMLAttributes: {
        class: 'rt-ordered-list',
      },
    }),
    ListItem.configure({
      HTMLAttributes: {
        class: 'rt-list-item',
      },
    }),
    TaskList.configure({
      HTMLAttributes: { class: 'rt-task-list' },
    }),
    TaskItem.configure({
      HTMLAttributes: {
        class: 'rt-task-item',
      },
      nested: true,
    }),
    // table stuff
    Table,
    // utils
    Dropcursor.configure({
      class: 'rt-dropcursor',
      color: '#FF6C00',
      width: 2,
    }),
    Gapcursor.configure({
      class: 'rt-gapcursor',
    }),
    HardBreak.configure({
      HTMLAttributes: {
        class: 'rt-hard-break',
      },
    }),
    Placeholder.configure({
      placeholder: ({ node }) => {
        if (node.type.name === 'heading') return `Heading ${node.attrs.level}`
        return "Write something, or press '/' for commands..."
      },
      includeChildren: true,
      emptyNodeClass: 'rt-empty-node',
    }),

    FakeCursor,
    Markdown,
    Typography,
    BubbleMenu.configure({
      element: document.querySelector('.menu') as HTMLElement,
    }),
    SlashCommands.configure({
      component: options.slashCommand.component,
      onSelectBlock: options.slashCommand.onSelectBlock,
      richTextId: options.slashCommand.richTextId,
    }),
    PasteHandlerExtension.configure({
      handlers: options.pasteHandlers ?? [],
    }),
    Underline.configure({
      HTMLAttributes: {
        class: 'rt-underline',
      },
    }),
    PasteExtension,
    DeleteFirstEmptyParagraphExtension,
    getLinterExtension(options.linterExtensionOptions),
    ...getCollaborationExtensions(options.collaborationExtensionsOptions),
  ] satisfies Extensions
}

/* -------------------------------------------------------------------------- */
/* Configurations for the extensions
/* -------------------------------------------------------------------------- */

const CustomVideo = Video.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
      },
      height: {
        default: null,
      },
    }
  },
  addProseMirrorPlugins() {
    return [UploadVideosPlugin()]
  },
})

const PasteExtension = Extension.create({
  name: 'transformPasted',
  addProseMirrorPlugins() {
    return [PasteImagePlugin()]
  },
})

const PasteImagePlugin = () =>
  new Plugin({
    props: {
      transformPastedHTML(html) {
        return fixTipTapContent(html)
      },
    },
  })

export function fixTipTapContent(html: string) {
  const container = document.createElement('div')
  container.innerHTML = html

  // 🔥 Unwrap images inside <span>, <b>, <div>, <p>, <i>, <em>, <strong>
  container
    .querySelectorAll(
      'span > img, b > img, div > img, p > img, i > img, em > img, strong > img'
    )
    .forEach((img) => unwrapParentPreservingOrder(img as HTMLElement))

  container.querySelectorAll('a > img').forEach((img) => {
    const link = img.parentElement
    if (!link) return

    const altText = img.getAttribute('alt') || 'Image link'
    const href = link.getAttribute('href') || '#'

    // Clonar la imagen y el <a> para poder moverlos sin conflicto
    const newImg = img.cloneNode(true)
    const newLink = document.createElement('a')
    newLink.href = href
    newLink.textContent = `🔗 ${altText}`

    const newParagraph = document.createElement('p')
    newParagraph.appendChild(newLink)

    // Insertamos después del contenedor original del <a>
    const insertAfter = (
      refNode: HTMLElement | Node,
      newNode: HTMLElement | Node
    ) => {
      if (refNode.parentNode) {
        if (refNode.nextSibling) {
          refNode.parentNode.insertBefore(newNode, refNode.nextSibling)
        } else {
          refNode.parentNode.appendChild(newNode)
        }
      }
    }

    // Insertar imagen y párrafo después del <a>
    insertAfter(link, newImg)
    insertAfter(newImg, newParagraph)

    // Borrar el <img> original (dentro del <a>)
    link.removeChild(img)

    // Si el <a> quedó vacío, lo eliminamos también
    if (!link.textContent?.trim()) {
      link.remove()
    }
  })

  // 🔥 Remove empty tags left behind after unwrapping
  container
    .querySelectorAll(
      'span:empty, b:empty, div:empty, p:empty, i:empty, em:empty, strong:empty'
    )
    .forEach((el) => el.remove())

  return container.innerHTML
}

/**
 * Moves all children out of the parent, keeping document order.
 */
function unwrapParentPreservingOrder(child: HTMLElement) {
  let parent = child.parentNode

  while (parent && shouldUnwrap(parent as HTMLElement)) {
    const grandparent = parent.parentNode
    if (!grandparent) {
      break // Avoid breaking the DOM structure

      // 🔥 Move all children of `parent` **above** `parent`
    }
    // eslint-disable-next-line no-extra-semi
    ;[...parent.childNodes].forEach((node) => {
      grandparent.insertBefore(node, parent)
    })

    // If parent is now empty, remove it
    if (parent.childNodes.length === 0) {
      grandparent.removeChild(parent)
    }

    parent = grandparent // Move up the tree
  }
}

/**
 * Determines whether an element should be unwrapped (i.e., if it's a formatting tag).
 */
function shouldUnwrap(el: HTMLElement) {
  return ['SPAN', 'B', 'I', 'EM', 'STRONG', 'DIV', 'P'].includes(el.tagName)
}
